import moment from 'moment';

import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MethodNode, ProcedureDataParameterPlannedValue} from 'procedures/ProcedureDomainTypes';
import {ProcedureNodeType} from 'procedures/ProcedureDomainTypes';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {ProcessNodeForValidation} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {readableTime} from 'util/DateUtil';

export function isMethodNode(
  node: TaskDefinitionVersionSpec<unknown>
): node is TaskDefinitionVersionSpec<MethodNode> {
  return (
    (node.newProcessTaskDefinitionVersion.applicationData as MethodNode | undefined)?.type ===
    ProcedureNodeType.METHOD
  );
}

export function isNodeValidInProcedureFlowchart(node: TaskDefinitionVersionSpec<unknown>): boolean {
  // TODO(BNCH-136029): We can't fully validate here until the WF platform provides the task schema. Without it, we'll miss ROOT nodes
  return true;
}

export function isStaticMethodNode(node: StaticFlowchartNodeData): node is TaskSchemaStaticNodeData & {
  taskDefinitionVersionSpec: {
    newProcessTaskDefinitionVersion: {
      applicationData: MethodNode;
    };
  };
} {
  return (
    isStaticTaskSchemaNodeData(node) &&
    node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType ===
      ProcessExecutionType.PROCEDURE_METHOD
  );
}

export function extractMethodNodesFromStaticFlowchart(
  staticFlowchart: StaticTemplateFlowchart
): Array<MethodNode> {
  return staticFlowchart.nodesStaticData
    .filter((node) => isStaticTaskSchemaNodeData(node))
    .filter((node) => isMethodNode(node.taskDefinitionVersionSpec))
    .flatMap(
      (node) => node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData as MethodNode
    );
}

export function extractMethodNodeFromProcessNode(processNode: ProcessNodeForValidation): MethodNode | null {
  if (
    processNode.nodeType !== ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
    processNode.executionType !== ProcessExecutionType.PROCEDURE_METHOD
  ) {
    return null;
  }

  const taskDefinitionVersionSpec = processNode.taskDefinitionVersionSpec;
  assert(
    isMethodNode(taskDefinitionVersionSpec),
    'Expected a task definition version spec to include a method node as application data'
  );
  return taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData;
}

/**
 * Format a setpoint value for data parameters to display in the node, including
 * the unit if a value is present. Returns null if there is no value to display.
 */
export function formatSetpointDisplayForNode(
  values: ReadonlyArray<ProcedureDataParameterPlannedValue> | undefined,
  unitSymbol: string | null | undefined,
  fieldType: FieldType
): string | null {
  const value = values != null && values.length > 0 ? (values[0].value.value ?? '') : '';
  if (!value) {
    return null;
  }

  const displayValue =
    fieldType === FieldType.DATETIME
      ? `${readableTime(value, {showYear: false, showTime: false})} ${moment(value).format('HH:mm')}`
      : value;
  const unit = unitSymbol ?? '';
  return `\u00A0${displayValue}\u00A0${unit}`;
}
